import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const WastePercentage = () => {
  // State variables for Waste Percentage calculation
  const [wasteQuantity, setWasteQuantity] = useState(0); // Waste quantity
  const [wasteUnit, setWasteUnit] = useState("kg"); // Unit for waste quantity
  const [totalInputQuantity, setTotalInputQuantity] = useState(0); // Total input quantity
  const [totalInputUnit, setTotalInputUnit] = useState("kg"); // Unit for total input quantity
  const [wastePercentage, setWastePercentage] = useState(0); // Calculated waste percentage

  // Conversion factors for units
  const unitConversion = {
    kg: 1,
    ton: 1000,
    lb: 0.453592,
    gram: 0.001,
  };

  // Event handlers for input changes
  const handleWasteQuantityChange = (e) =>
    setWasteQuantity(parseFloat(e.target.value) || 0);
  const handleWasteUnitChange = (e) => setWasteUnit(e.target.value);
  const handleTotalInputQuantityChange = (e) =>
    setTotalInputQuantity(parseFloat(e.target.value) || 0);
  const handleTotalInputUnitChange = (e) => setTotalInputUnit(e.target.value);

  // Calculate Waste Percentage based on selected units
  useEffect(() => {
    if (totalInputQuantity > 0) {
      const normalizedWaste = wasteQuantity * unitConversion[wasteUnit];
      const normalizedTotalInput =
        totalInputQuantity * unitConversion[totalInputUnit];
      const percentage = (normalizedWaste / normalizedTotalInput) * 100;
      setWastePercentage(percentage);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [wasteQuantity, wasteUnit, totalInputQuantity, totalInputUnit]);

  return (
    <Layout title="Waste Percentage Calculator" path="waste-percentage">
      <section className="section-padding" id="waste-percentage">
        <div className="container mt-5">
          <div className="row">
            {/* Waste Percentage Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Waste Percentage Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Waste Quantity */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="wasteQuantity" className="form-label">
                        Waste Quantity
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Waste Quantity:</strong> The amount of material that is considered as waste in the process."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="wasteQuantity"
                        placeholder="Enter waste quantity"
                        value={wasteQuantity || ""}
                        onChange={handleWasteQuantityChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={wasteUnit}
                        onChange={handleWasteUnitChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="ton">Tons</option>
                        <option value="lb">Pounds (lb)</option>
                        <option value="gram">Grams (g)</option>
                      </select>
                    </div>

                    {/* Input for Total Input Quantity */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="totalInputQuantity"
                        className="form-label"
                      >
                        Total Input Quantity
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Input Quantity:</strong> The total amount of material used in the production process before waste occurs."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalInputQuantity"
                        placeholder="Enter total input quantity"
                        value={totalInputQuantity || ""}
                        onChange={handleTotalInputQuantityChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={totalInputUnit}
                        onChange={handleTotalInputUnitChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="ton">Tons</option>
                        <option value="lb">Pounds (lb)</option>
                        <option value="gram">Grams (g)</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Waste Percentage:{" "}
                        <span className="fw-bold ms-3">
                          {wastePercentage === 0
                            ? "0"
                            : wastePercentage.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Waste Percentage Calculation
                </h3>

                {/* What is Waste Percentage? */}
                <h4 className="mt-3 mb-3">What is Waste Percentage?</h4>
                <p>
                  **Waste Percentage** refers to the proportion of materials
                  wasted during a production process compared to the total input
                  materials used. It is a critical metric for assessing
                  efficiency and sustainability.
                </p>

                {/* Why Use the Waste Percentage Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Waste Percentage Calculator?
                </h4>
                <p>
                  This calculator provides insights into material wastage,
                  helping organizations reduce waste, lower costs, and enhance
                  production efficiency.
                </p>

                {/* Benefits of Using the Waste Percentage Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Waste Percentage Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Reduces Material Costs:</strong> Identifies areas
                    where material usage can be optimized.
                  </li>
                  <li>
                    <strong>Improves Sustainability:</strong> Helps minimize
                    environmental impact by reducing waste.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Waste Percentage Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing:</strong> Tracks material efficiency
                    in production processes.
                  </li>
                  <li>
                    <strong>Production:</strong> Identifies bottlenecks leading
                    to material waste.
                  </li>
                  <li>
                    <strong>Quality Control:</strong> Monitors waste levels to
                    maintain high-quality standards.
                  </li>
                </ul>

                {/* Question 1 */}
                <h5 className="mt-3 mb-2">
                  How can reducing waste improve profitability?
                </h5>
                <p>
                  Reducing waste lowers material costs and improves operational
                  efficiency, directly contributing to increased profitability.
                  It also aligns with sustainability goals, enhancing brand
                  reputation.
                </p>

                {/* Question 2 */}
                <h5 className="mt-3 mb-2">
                  Can this calculator be used for industries outside
                  manufacturing?
                </h5>
                <p>
                  Yes, this calculator can be adapted for industries such as
                  food production, logistics, and construction to measure and
                  manage waste effectively.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WastePercentage;
